import { useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get_automation_runs_with_datasets_url } from '@/utils/urls.ts';
import { AUTOMATION_RUNS_WITH_DATASETS_KEY } from '@/utils/queryKeys.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import type { ExecutionStatus } from '@/types/statuses.ts';
import {
  getRunProvenanceQueryStates,
  isDefaultRunStatusSelection,
  RunMode,
  RunOrigin,
  RunProvenanceState,
  RunsWithDatasets,
} from '@/types/runs.ts';

type QueryProps = {
  statuses?: ExecutionStatus[];
  search?: string;
  providedAutomationId?: string;
  runModes?: RunMode[];
  runOrigins?: RunOrigin[];
  provenanceStates?: RunProvenanceState[];
};

const normalizeFilterValues = <T extends string>(values: T[] | undefined): T[] =>
  [...new Set(values ?? [])].sort();

const useRetrieveAutomationRunsWithDatasets = ({
  statuses,
  search,
  providedAutomationId,
  runModes,
  runOrigins,
  provenanceStates,
}: QueryProps) => {
  const apiClient = useApiClient();
  const detectedAutomationId = useAutomationId();
  const automationId = providedAutomationId || detectedAutomationId;
  const normalizedRunModes = normalizeFilterValues(runModes ?? [RunMode.Operational]);
  const normalizedStatuses = normalizeFilterValues(statuses);
  const statusQueryValues = isDefaultRunStatusSelection(normalizedStatuses)
    ? []
    : normalizedStatuses;
  const normalizedRunOrigins = normalizeFilterValues(runOrigins);
  const normalizedProvenanceStates = normalizeFilterValues(provenanceStates);
  const provenanceQueryStates = getRunProvenanceQueryStates(normalizedProvenanceStates);

  return useInfiniteQuery<RunsWithDatasets, AxiosError>({
    queryKey: [
      ...AUTOMATION_RUNS_WITH_DATASETS_KEY(automationId),
      statusQueryValues.join(','),
      search,
      normalizedRunModes.join(','),
      normalizedRunOrigins.join(','),
      provenanceQueryStates.join(','),
    ],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      statusQueryValues.forEach((value) => params.append('status', value));
      if (search) params.append('search', search);
      normalizedRunModes.forEach((value) => params.append('run_mode', value));
      normalizedRunOrigins.forEach((value) => params.append('run_origin', value));
      provenanceQueryStates.forEach((value) => params.append('provenance_state', value));
      params.append('page', (pageParam as number).toString());

      const response = await apiClient.get<RunsWithDatasets>(
        get_automation_runs_with_datasets_url(automationId),
        { params },
      );

      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.next?.page ?? undefined,
    initialPageParam: 1,
    enabled: !!automationId,
  });
};

export default useRetrieveAutomationRunsWithDatasets;
